/**
 * THE CONSENT RECORD. FIRST-PARTY, THIS ORIGIN ONLY, VERSIONED.
 *
 * `localStorage` on book.uselura.com and nothing else. Not a cookie on
 * `.uselura.com`, which would carry an answer given on one product into
 * another; not a value read from `app.*` or from the marketing site; not
 * anything inferred from a Firebase session or an anonymous uid. A person
 * booking with a hairdresser has not agreed to anything on any other Lura
 * surface, and a shared parent-domain cookie would quietly assume they had.
 *
 * FOUR STATES REACH THE READER AND THREE OF THEM MEAN OFF.
 *
 *   granted    an affirmative answer, recorded at a known version
 *   denied     an affirmative refusal
 *   unknown    never asked, or asked and not answered
 *   unreadable storage threw, or held something this version does not
 *              understand
 *
 * Only `granted` permits collection. That is not a stylistic preference: the
 * common bug is a `catch {}` that returns the last-known value or a default,
 * and every one of those defaults eventually resolves toward collecting from
 * someone whose answer could not be read.
 *
 * WHAT IS NOT STORED. No identifier, no page, no professional, no timestamped
 * trail beyond the single decision itself. Whether even the decision's
 * timestamp may be retained, and for how long, is Legal/Privacy's call — see
 * docs/analytics/book-ga4-foundation.md. Until they answer, this stores the
 * version and the answer and nothing else.
 */

// `handle()`, not `get()`: this record must tell a missing answer (`unknown`)
// from storage that threw (`unreadable`), and `get()` reads both as null.
// Accessing the `localStorage` property is itself what throws under a
// blocked-site-data policy; `handle()` returns null for that.
import { safeStorage } from "../../../../shared/lib/safeStorage";

/**
 * Bump when the QUESTION changes, never when the code does.
 *
 * A version is a claim about what the person was asked. Reusing v1 after
 * changing the wording or the scope of the request would let an answer to the
 * old question authorise the new one; an unrecognised version reads as
 * `unreadable`, which means off, which means they get asked again.
 */
export const CONSENT_VERSION = 1;

/** Scoped to this surface and this version, so nothing else can collide. */
export const CONSENT_KEY = "lura.book.analytics-consent";

export type ConsentChoice = "granted" | "denied";
export type ConsentState = ConsentChoice | "unknown" | "unreadable";

export interface ConsentRecord {
  readonly version: number;
  readonly choice: ConsentChoice;
}

/** The one place that decides whether a stored value may be believed. */
function parse(raw: string | null): ConsentState {
  if (raw === null) return "unknown";
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return "unreadable";
    const record = value as Partial<ConsentRecord>;
    if (record.version !== CONSENT_VERSION) return "unreadable";
    if (record.choice !== "granted" && record.choice !== "denied") return "unreadable";
    return record.choice;
  } catch {
    return "unreadable";
  }
}

/**
 * Read the answer. Never throws.
 *
 * `localStorage` is not merely empty in a private window or with site data
 * blocked — the accessor itself throws in some configurations. A read that
 * can throw is a read that can take the surface down, and a consent lookup
 * that takes the booking down would be a worse outcome than not measuring.
 */
export function readConsent(storage?: Storage | null): ConsentState {
  // `null` means "there is none", `undefined` means "find it". A default
  // parameter cannot express both: JavaScript fires the default for
  // `undefined`, so `readConsent(undefined)` would silently reach for real
  // storage — which is exactly what the first version of this did, and the
  // test that claimed to cover "no storage at all" was passing against
  // `localStorage`.
  const store = storage === undefined ? safeStorage.local.handle() : storage;
  if (!store) return "unreadable";
  try {
    return parse(store.getItem(CONSENT_KEY));
  } catch {
    return "unreadable";
  }
}

/** Only an affirmative, current-version `granted` is permission. */
export function isGranted(state: ConsentState): boolean {
  return state === "granted";
}

/**
 * Record an answer. Returns whether it was actually persisted.
 *
 * The boolean is the point, and it is why this does not return void. A save
 * that failed must be visible to the caller so the screen can say so and stay
 * off — "we could not save your setting, analytics remain off" is a true
 * sentence only if the caller knows. Swallowing the failure and rendering
 * success would tell someone their choice was kept when it was not, and if
 * that choice was `granted` it would also be collecting on an unrecorded
 * answer.
 */
export function writeConsent(choice: ConsentChoice, storage?: Storage | null): boolean {
  const store = storage === undefined ? safeStorage.local.handle() : storage;
  if (!store) return false;
  const record: ConsentRecord = { version: CONSENT_VERSION, choice };
  try {
    store.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch {
    // Quota, private mode, blocked site data. Every one of them means the
    // answer was not kept.
    return false;
  }
  // Trust nothing that was not read back. A `setItem` that resolved and stored
  // nothing is a real browser behaviour, and it is indistinguishable from
  // success without this.
  return readConsent(store) === choice;
}

/**
 * Expire the GA cookies this surface caused to be set, as far as a page can.
 *
 * "As far as a page can" is the honest scope and the comment says so rather
 * than the code implying more. A page may only expire cookies it can name and
 * whose domain and path it can guess; GA writes `_ga` and `_ga_<container>` at
 * the registrable domain by default, so both the exact hostname and the
 * dotted parent are attempted. Anything HttpOnly, anything on a domain this
 * page cannot address, and anything a future GA version names differently is
 * out of reach from here — which is why withdrawal ALSO stops the library
 * being loaded at all on the next page, and does not depend on this succeeding.
 */
export function clearAnalyticsCookies(doc: Document | undefined = safeDocument()): void {
  if (!doc) return;
  let names: string[];
  try {
    names = doc.cookie
      .split(";")
      .map((pair) => pair.split("=")[0]?.trim() ?? "")
      .filter((name) => name === "_ga" || name.startsWith("_ga_"));
  } catch {
    return;
  }
  const host = doc.location?.hostname ?? "";
  // The exact host, then each parent up to the registrable domain. `uselura.com`
  // is reached as `.uselura.com`; the public suffix itself is never attempted.
  const labels = host.split(".");
  const domains = [""];
  for (let i = 0; i + 2 < labels.length + 1; i += 1) {
    const candidate = labels.slice(i).join(".");
    if (candidate.split(".").length >= 2) domains.push(`; domain=.${candidate}`);
  }
  for (const name of names) {
    for (const domain of domains) {
      try {
        doc.cookie = `${name}=; max-age=0; path=/${domain}`;
      } catch {
        // A cookie we cannot expire is one the next page load will not use
        // either, because nothing will be loaded to read it.
      }
    }
  }
}

function safeDocument(): Document | undefined {
  try {
    return typeof document === "undefined" ? undefined : document;
  } catch {
    return undefined;
  }
}
